import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';

import {
  useGetAllVehicules,
  useUpdateManyVehicules,
  useImportVehicles,
} from '@/hooks/vehicule.hooks';

import { TVehiculeSchema } from '@/schemas/vehicule/vehicule.schema';

import { PageHeader } from '@/components/Header/Page/PageHeader';
import { PageInfoHeading } from '@/components/Header/Page/PageInfoHeading';
import { VehiculeTableView } from '../components/VehiculeTableView';
import { VehiculeTableHeaders } from '../components/VehiculeTableHeaders';
import { VehiculeImportModal } from '@/components/Custom/Modal/VehiculeImportModal';
import { VehiculeTableData } from '../components/VehiculeTableData';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import Modal from '@/components/Modal';
import { useDeactivateField } from '@/hooks/deactivate-field.hooks';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import { ImportIcon } from '@/components/SvgIcons/ImportIcon';
import { useUserRole } from '@/hooks/auth.hooks';
import { WarningMessage } from '@/components/Message/WarningMessage';

import { showModal } from '@/utils/modal.utils';
import { mapVehiclesImport } from '@/utils/map.utils';

export const VehiculeListPage = () => {
  const { t } = useTranslation(['vehicule', 'inspection']);
  const navigate = useNavigate();
  const [totalActive, setTotalActive] = useState<number>(0);
  const [vehicleImport, setVehicleImport] = useState<File>();
  const [vehiculeOwner, setVehiculeOwner] = useState<TVehiculeSchema | null>(
    null
  );
  const { isAdmin, isGroupManager, isCompanyManager } = useUserRole();
  const { mutate: importVehicles } = useImportVehicles({ t });

  /**
   * === Deactivate Data section ===
   */
  const {
    numberOfItemsToDeactivate,
    pendingDeactivate,
    handleDeactivate,
    onConfirmDeactivate,
  } = useDeactivateField<TVehiculeSchema>({
    t,
    linkedFieldNames: ['vehiculeChecks'],
    useUpdateData: useUpdateManyVehicules,
  });
  /**
   * === END OF Deactivate Data section ===
   */

  const onGetTotalActive = (total: number) => {
    setTotalActive(total);
  };

  const handleClickImport = () => {
    if (!vehicleImport) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const workbook = XLSX.read(text, { type: 'string' });

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      const vehiclesMapped = mapVehiclesImport(
        jsonData as object[],
        vehiculeOwner
      );
      importVehicles(vehiclesMapped as unknown as TVehiculeSchema[]);
    };
    // reader.readAsText(vehicleImport);
    reader.readAsArrayBuffer(vehicleImport);
  };

  const displayHeadButton = isAdmin || isGroupManager || isCompanyManager;

  return (
    <>
      <PageHeader
        title={t('common:vehicules')}
        totalItems={totalActive}
        headingElement={
          <PageInfoHeading text={t('common:detailsRelatedToCurrentSection')} />
        }
        actionListElement={
          displayHeadButton && (
            <>
              <HeadingButton
                text={t('common:importAList')}
                leftIcon={
                  <ImportIcon className="-mt-[1.5px] w-[15.5px] h-[17px] [&>path]:fill-light-inverse dark-mode:[&>path]:fill-gray-600" />
                }
                onClick={() => showModal('#vehicule-import-modal')}
              />
              <HeadingButton
                text={t('addVehicule')}
                leftIcon={
                  <i className="-mt-[1.5px] ki-outline ki-plus-circle !text-[19.4px] text-light-inverse dark-mode:text-gray-600" />
                }
                onClick={() => navigate('/vehicule/create')}
              />
            </>
          )
        }
      />
      <div>
        <VehiculeTableView<TVehiculeSchema>
          t={t}
          useGetAllData={useGetAllVehicules}
          onGetTotalActive={onGetTotalActive}
          tableHeaders={VehiculeTableHeaders}
          tableData={VehiculeTableData}
          pendingDeactivate={pendingDeactivate}
          onDeactivate={handleDeactivate}
        />
      </div>
      <Modal
        id="information-modal"
        title={t('common:information')}
        centerY
        showCancelBtn={false}
        showSecondBtn={true}
        secondBtnLabel="Ok"
        size="small"
      >
        <WarningMessage message={t('common:unableToDeactivateLinkedItem')} />
      </Modal>
      <ConfirmationModal
        id="deactivation-confirm-modal"
        title={t('common:confirmation')}
        description={
          numberOfItemsToDeactivate > 1 ? (
            <Trans i18nKey="common:confirmDeactivateItems" />
          ) : (
            t('common:confirmDeactivateItem')
          )
        }
        onConfirmLabel={t('common:deactivate')}
        onConfirm={onConfirmDeactivate}
      />
      <VehiculeImportModal
        id="vehicule-import-modal"
        t={t}
        onConfirm={handleClickImport}
        confirmLabel={t('common:save')}
        onFileChange={(file) => setVehicleImport(file)}
        setVehiculeOwner={setVehiculeOwner}
      />
    </>
  );
};
